import React from 'react';
import type { HistoryEntry } from '../../services/supportApi';

// Nome leggibile di un richiedente nella meta della cronologia (Task 23/C).
const requesterName = (r?: { nome?: string; email?: string }): string => (r && (r.nome || r.email)) || '';

// Etichette leggibili per la cronologia (audit trail) del ticket
const HISTORY_LABELS: Record<string, (e: { byName?: string; meta?: any }) => string> = {
  created: (e) => `Ticket aperto da ${e.byName || 'associato'}`,
  status_changed: (e) => `Stato: ${e.meta?.from || '?'} → ${e.meta?.to || '?'} (${e.byName || 'sistema'})`,
  assignee_added: (e) =>
    e.meta?.auto
      ? `Preso in carico (automatico) da ${e.meta?.nome || e.byName || '?'} con la risposta al richiedente`
      : e.meta?.external
      ? `Assegnato (esterno al canale) a ${e.meta?.nome || '?'} da ${e.byName || '?'}`
      : `${e.meta?.nome || 'Componente'} aggiunto da ${e.byName || '?'}`,
  assignee_removed: (e) => `Componente rimosso da ${e.byName || '?'}`,
  transferred: (e) => `Trasferito da ${e.byName || '?'}${e.meta?.motivo ? ` — ${e.meta.motivo}` : ''}`,
  reopened: (e) => `Riaperto da ${e.byName || 'associato'}`,
  rated: (e) => `Valutato ${e.meta?.stars ?? '?'}★ dall'associato`,
  requester_set: (e) =>
    `Richiedente impostato da ${e.byName || '?'}${requesterName(e.meta?.to) ? `: ${requesterName(e.meta?.to)}` : ''}`,
  requester_changed: (e) =>
    `Richiedente cambiato da ${requesterName(e.meta?.from) || '?'} a ${requesterName(e.meta?.to) || '?'} (${e.byName || '?'})`,
  requester_notified: (e) =>
    `Email di apertura inviata al richiedente dopo lo smistamento${e.meta?.groupName ? ` (canale ${e.meta.groupName})` : ''}`,
  closed_manually: (e) =>
    `Chiuso manualmente da ${e.byName || '?'}${e.meta?.motivo ? ` — ${e.meta.motivo}` : ''}`,
  auto_closed: (e) => `Chiusa automaticamente dopo ${e.meta?.days ?? 14} giorni`,
  sla_breached: (e) => `SLA ${e.meta?.which === 'firstResponse' ? 'prima risposta' : 'risoluzione'} sforato`,
  merged_from: (e) => `Ticket #${e.meta?.ticketNumber || '?'} unito qui da ${e.byName || '?'}`,
  sender_ignored: (e) => `Mittente ${e.meta?.email || '?'} ignorato da ${e.byName || '?'}`,
  merged_into: (e) => `Unito al ticket #${e.meta?.ticketNumber || '?'} da ${e.byName || '?'}`,
  tags_changed: (e) =>
    `Tag aggiornati da ${e.byName || '?'}${e.meta?.tags?.length ? `: ${e.meta.tags.join(', ')}` : ' (rimossi tutti)'}`,
  priority_changed: (e) => `Priorità: ${e.meta?.from || '?'} → ${e.meta?.to || '?'} (${e.byName || '?'})`,
  subject_changed: (e) =>
    `Oggetto modificato da ${e.byName || '?'}${e.meta?.before ? ` — prima: ${e.meta.before}` : ''}`,
  // «Dati della pratica» del canale Documento Unico (27/10/2026).
  // meta: { prima, dopo }, null quando l'insieme è vuoto.
  campi_extra_updated: (e) => `Dati della pratica modificati da ${e.byName || '?'}`,
  attachments_shared: (e) =>
    `Link a ${e.meta?.count ?? '?'} allegat${e.meta?.count === 1 ? 'o' : 'i'} condivisi via email da ${e.byName || '?'}${
      e.meta?.expiresAt ? ` (validi fino al ${new Date(e.meta.expiresAt).toLocaleDateString('it-IT')})` : ' (senza scadenza)'
    }`,
  message_edited: (e) =>
    `Messaggio modificato da ${e.byName || '?'}${
      e.meta?.authorType === 'associate' || e.meta?.authorType === 'guest' ? ' (del richiedente)' : ' (del canale)'
    }`,
  // Note interne (24/10/2026): correzione ed eliminazione, solo vista operatore.
  note_edited: (e) => `Nota interna modificata da ${e.byName || '?'}`,
  note_deleted: (e) => `Nota interna eliminata da ${e.byName || '?'}`,
  // Risposte in bozza (Task 46): solo vista operatore.
  reply_drafted: (e) => `Risposta in bozza scritta da ${e.byName || '?'}, in attesa di approvazione`,
  reply_approved: (e) =>
    `Bozza di ${e.meta?.authorName || '?'} approvata${e.meta?.edited ? ' con modifiche' : ''} da ${e.byName || '?'} e inviata al richiedente`,
  reply_rejected: (e) =>
    `Bozza di ${e.meta?.authorName || '?'} rifiutata da ${e.byName || '?'}${e.meta?.note ? ` — ${e.meta.note}` : ''}`,
  reply_draft_deleted: (e) => `Bozza ritirata da ${e.byName || '?'}`,
  attachment_removed: (e) =>
    `Allegato${e.meta?.filename ? ` "${e.meta.filename}"` : ''} rimosso da ${e.byName || '?'}${
      e.meta?.authorType === 'associate' || e.meta?.authorType === 'guest' ? ' (messaggio del richiedente)' : ' (messaggio del canale)'
    }`,
  // Partecipanti invitati (spec 2026-10-15, parte B): solo vista operatore.
  participant_added: (e) =>
    `${e.meta?.nome || '?'} invitata/o nel ticket da ${e.byName || '?'}${e.meta?.via === 'mention' ? ' (menzione in una nota)' : ''}`,
  participant_removed: (e) => `${e.meta?.nome || '?'} tolta/o dai partecipanti da ${e.byName || '?'}`,
  // Canali in copia (spec 2026-10-15, parte A): solo vista operatore.
  copy_group_added: (e) => `Canale ${e.meta?.groupName || '?'} messo in copia da ${e.byName || '?'}`,
  copy_group_removed: (e) => `Canale ${e.meta?.groupName || '?'} tolto dalla copia da ${e.byName || '?'}`,
  responsible_changed: (e) =>
    `Responsabile cambiato da ${e.meta?.fromGroupName || '?'} a ${e.meta?.toGroupName || '?'} (${e.byName || '?'})${
      e.meta?.oldInCopy ? `; ${e.meta?.fromGroupName || 'il canale precedente'} resta in copia` : ''
    }${e.meta?.motivo ? ` — ${e.meta.motivo}` : ''}`,
};

export function historyLabel(e: { type: string; byName?: string; meta?: any }): string {
  const fn = HISTORY_LABELS[e.type];
  return fn ? fn(e) : e.type;
}

interface HistoryTimelineProps {
  history?: HistoryEntry[];
}

// Cronologia del ticket (solo operatori), dalla voce più recente.
const HistoryTimeline: React.FC<HistoryTimelineProps> = ({ history }) => {
  if (!history || history.length === 0) return null;
  return (
    <div className="mt-4 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Cronologia</h3>
      <ol className="space-y-1">
        {[...history].reverse().map((e, i) => (
          <li key={i} className="flex items-baseline gap-2 text-xs text-gray-600 dark:text-gray-300">
            <span className="shrink-0 text-gray-400 dark:text-gray-500 tabular-nums">
              {new Date(e.at).toLocaleString('it-IT', {
                day: '2-digit',
                month: '2-digit',
                year: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
            <span>{historyLabel(e)}</span>
          </li>
        ))}
      </ol>
    </div>
  );
};

export default HistoryTimeline;
